import React, { useCallback, useRef, useEffect, useMemo, useImperativeHandle, forwardRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import '../styles/RepairCostsImageGallery.css';

const RepairCostsImageGallery = forwardRef((props, ref) => {
    const {
        images = [],
        categoryLabelMap = new Map(),
        onImageClick = null,
        enablePerformanceLogging = false
    } = props;

    const containerRef = useRef(null);

    // Responsive: detect if we should use grid layout (2 columns on larger screens)
    const [useGridLayout, setUseGridLayout] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth >= 1920;
        }
        return false;
    });

    useEffect(() => {
        const handleResize = () => {
            setUseGridLayout(window.innerWidth >= 1920);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Ensure images is always an array
    const imageList = useMemo(() => {
        if (!images) return [];
        if (!Array.isArray(images)) return [];
        return images.filter(img => img != null && img.url != null);
    }, [images]);

    // Grid layout constants (for larger screens) - matching ScrollableImages.js
    const COLUMN_COUNT = 2;
    const GRID_WIDTH = 750; // Fixed width matching ScrollableImages
    const CELL_PADDING = 8; // 8px padding on each side = 16px visual gap between columns
    const COLUMN_WIDTH = useMemo(() => {
        // react-window columnWidth is the full column width
        // With 2 columns totaling 750px: 750 / 2 = 375px per column
        // Padding (8px each side) is applied in the Cell component
        return useGridLayout ? GRID_WIDTH / COLUMN_COUNT : '100%';
    }, [useGridLayout]);

    // Calculate row height - estimate based on typical image aspect ratio
    // Matching ScrollableImages.js approach: (column width - padding) * aspect ratio + padding
    // Using max-height of 500px from CSS as constraint
    const LABEL_HEIGHT = 24; // Approximate height for body-regular text
    const LABEL_GAP = 8;
    const IMAGE_GAP = 8; // Gap between images
    const BASE_ROW_HEIGHT = useMemo(() => {
        // Single column: 500px max-height (gap is handled by paddingBottom)
        return 500;
    }, []);
    const BASE_ROW_HEIGHT_GRID = useMemo(() => {
        // Grid layout: calculate based on aspect ratio like ScrollableImages
        // Account for padding: (column width - padding) * aspect ratio + padding
        const imageWidth = COLUMN_WIDTH - (CELL_PADDING * 2);
        const calculatedHeight = imageWidth * 0.75 + (CELL_PADDING * 2);
        // Cap at 500px max-height from CSS
        return Math.min(calculatedHeight, 500 + (CELL_PADDING * 2));
    }, [COLUMN_WIDTH]);
    const ROW_HEIGHT_WITH_LABEL = BASE_ROW_HEIGHT + LABEL_HEIGHT + LABEL_GAP;
    const ROW_HEIGHT_WITH_LABEL_GRID = BASE_ROW_HEIGHT_GRID + LABEL_HEIGHT + LABEL_GAP;

    // Determine which images need labels (first image of each category)
    const imagesWithLabels = useMemo(() => {
        const set = new Set();
        let lastCategoryKey = null;
        
        imageList.forEach((img, index) => {
            const currentCategoryKey = img.categoryKey || null;
            if (currentCategoryKey && currentCategoryKey !== lastCategoryKey) {
                set.add(index);
                lastCategoryKey = currentCategoryKey;
            }
        });
        
        return set;
    }, [imageList]);

    // For grid layout: calculate rows (each row has 2 images)
    // For single column: each image is its own row
    const ROW_COUNT = useMemo(() => {
        if (useGridLayout) {
            return Math.ceil(imageList.length / COLUMN_COUNT);
        }
        return imageList.length;
    }, [imageList.length, useGridLayout]);

    // Calculate row height for each row index
    const getRowHeight = useCallback((rowIndex) => {
        if (!useGridLayout) {
            // Single column: check if this image index has a label
            // paddingBottom on row div handles spacing between images
            const baseHeight = imagesWithLabels.has(rowIndex) ? ROW_HEIGHT_WITH_LABEL : BASE_ROW_HEIGHT;
            return baseHeight;
        }
        
        // Grid layout: check if any image in this row has a label
        const firstImageIndex = rowIndex * COLUMN_COUNT;
        const hasLabel = imagesWithLabels.has(firstImageIndex);
        return hasLabel ? ROW_HEIGHT_WITH_LABEL_GRID : BASE_ROW_HEIGHT_GRID;
    }, [useGridLayout, imagesWithLabels, ROW_HEIGHT_WITH_LABEL, BASE_ROW_HEIGHT, ROW_HEIGHT_WITH_LABEL_GRID, BASE_ROW_HEIGHT_GRID]);

    const rowVirtualizer = useVirtualizer({
        count: ROW_COUNT,
        getScrollElement: () => containerRef.current,
        estimateSize: getRowHeight,
        overscan: 8, // Same as ScrollableImages
        getItemKey: (index) => {
            if (useGridLayout) {
                return `row-${index}`;
            }
            const imageData = imageList[index];
            return imageData?.id || imageData?.url || `image-${index}`;
        },
    });

    // Expose scrollToImage function via ref
    useImperativeHandle(ref, () => ({
        scrollToImage: (imageUrl) => {
            if (!containerRef.current || !imageUrl) return;
            
            // Find the index of the image with matching URL
            const imageIndex = imageList.findIndex(img => img.url === imageUrl);
            if (imageIndex === -1) return;
            
            // Calculate scroll position
            let scrollPosition = 0;
            if (useGridLayout) {
                // Grid layout: find which row contains this image
                const targetRow = Math.floor(imageIndex / COLUMN_COUNT);
                for (let i = 0; i < targetRow; i++) {
                    scrollPosition += getRowHeight(i);
                }
            } else {
                // Single column: sum heights of all previous images
                for (let i = 0; i < imageIndex; i++) {
                    scrollPosition += getRowHeight(i);
                }
            }
            
            // Scroll to the position
            containerRef.current.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
            });
        },
        scrollToImageByIndex: (index) => {
            if (!containerRef.current || index < 0 || index >= imageList.length) return;
            
            // Calculate scroll position
            let scrollPosition = 0;
            if (useGridLayout) {
                // Grid layout: find which row contains this image
                const targetRow = Math.floor(index / COLUMN_COUNT);
                for (let i = 0; i < targetRow; i++) {
                    scrollPosition += getRowHeight(i);
                }
            } else {
                // Single column: sum heights of all previous images
                for (let i = 0; i < index; i++) {
                    scrollPosition += getRowHeight(i);
                }
            }
            
            // Scroll to the position
            containerRef.current.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
            });
        }
    }), [imageList, useGridLayout, getRowHeight, COLUMN_COUNT]);

    // Performance monitoring for scroll events (optional)
    useEffect(() => {
        if (!enablePerformanceLogging || !containerRef.current) return;

        const wrapper = containerRef.current;
        let scrollTimeout = null;
        let lastScrollTime = performance.now();
        let frameCount = 0;

        const handleScroll = () => {
            const now = performance.now();
            frameCount++;
            
            // Log FPS every 500ms
            if (now - lastScrollTime >= 500) {
                const fps = (frameCount / (now - lastScrollTime)) * 1000;
                console.log(`[RepairCostsImageGallery Performance] Scroll FPS: ${fps.toFixed(1)}, Total images: ${imageList.length}`);
                frameCount = 0;
                lastScrollTime = now;
            }
            
            // Clear timeout for scroll end detection
            if (scrollTimeout) clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                console.log(`[RepairCostsImageGallery Performance] Scroll ended. Total images: ${imageList.length}`);
            }, 150);
        };

        wrapper.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            wrapper.removeEventListener('scroll', handleScroll);
            if (scrollTimeout) clearTimeout(scrollTimeout);
        };
    }, [enablePerformanceLogging, imageList.length]);

    if (!imageList || imageList.length === 0) {
        return null;
    }

    return (
        <div className="repair-costs-image-gallery-container">
            <div 
                ref={containerRef}
                className="repair-costs-image-gallery-wrapper"
                style={{ 
                    height: '100%', 
                    overflowY: 'auto',
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <div 
                    style={{ 
                        height: rowVirtualizer.getTotalSize(), 
                        position: 'relative', 
                        width: useGridLayout ? GRID_WIDTH : '100%',
                        maxWidth: useGridLayout ? GRID_WIDTH : '644px',
                        margin: '0 auto'
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        if (useGridLayout) {
                            // Grid layout: render 2 images per row using absolute positioning (matching ScrollableImages.js)
                            const rowIndex = virtualRow.index;
                            const firstImageIndex = rowIndex * COLUMN_COUNT;
                            const hasLabel = imagesWithLabels.has(firstImageIndex);
                            const categoryLabel = hasLabel ? categoryLabelMap.get(imageList[firstImageIndex]?.categoryKey) : null;
                            const labelOffset = hasLabel ? LABEL_HEIGHT + LABEL_GAP : 0;

                            return (
                                <div
                                    key={virtualRow.key}
                                    style={{
                                        position: 'absolute',
                                        top: virtualRow.start,
                                        left: 0,
                                        width: '100%',
                                        height: virtualRow.size
                                    }}
                                >
                                    {hasLabel && categoryLabel && (
                                        <div 
                                            className="repair-costs-image-gallery-label" 
                                            style={{ 
                                                width: '100%',
                                                marginBottom: `${LABEL_GAP}px`,
                                                paddingLeft: CELL_PADDING,
                                                paddingRight: CELL_PADDING
                                            }}
                                        >
                                            <span className="body-regular colour-primary">
                                                {categoryLabel}
                                            </span>
                                        </div>
                                    )}
                                    {[0, 1].map((col) => {
                                        const imageIndex = firstImageIndex + col;
                                        const imageData = imageList[imageIndex];
                                        if (!imageData) return null;
                                        const left = col * COLUMN_WIDTH;

                                        return (
                                            <div
                                                key={`${rowIndex}-${col}-${imageIndex}`}
                                                className="repair-costs-image-gallery-cell"
                                                style={{
                                                    position: 'absolute',
                                                    left,
                                                    top: labelOffset,
                                                    width: COLUMN_WIDTH,
                                                    height: BASE_ROW_HEIGHT_GRID,
                                                    padding: CELL_PADDING,
                                                    boxSizing: 'border-box'
                                                }}
                                            >
                                    <img
                                        src={imageData.url}
                                        alt={`Repair area ${imageData.areaKey || ''}`}
                                        className="repair-costs-image-gallery-image"
                                        data-area-key={imageData.areaKey}
                                        loading="lazy"
                                        decoding="async"
                                        onClick={() => onImageClick && onImageClick(imageData.url)}
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover',
                                            maxHeight: 'none' // Override CSS max-height for grid layout
                                        }}
                                    />
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        } else {
                            // Single column layout
                            const imageData = imageList[virtualRow.index];
                            if (!imageData) return null;

                            const hasLabel = imagesWithLabels.has(virtualRow.index);
                            const categoryLabel = hasLabel ? categoryLabelMap.get(imageData.categoryKey) : null;

                            return (
                                <div
                                    key={virtualRow.key}
                                    style={{
                                        position: 'absolute',
                                        top: virtualRow.start,
                                        left: 0,
                                        height: virtualRow.size,
                                        width: '100%',
                                        paddingBottom: `${IMAGE_GAP}px`
                                    }}
                                >
                                    {hasLabel && categoryLabel && (
                                        <div className="repair-costs-image-gallery-label" style={{ marginBottom: '8px' }}>
                                            <span className="body-regular colour-primary">
                                                {categoryLabel}
                                            </span>
                                        </div>
                                    )}
                                    <img
                                        src={imageData.url}
                                        alt={`Repair area ${imageData.areaKey || ''}`}
                                        className="repair-costs-image-gallery-image"
                                        data-area-key={imageData.areaKey}
                                        loading="lazy"
                                        decoding="async"
                                        onClick={() => onImageClick && onImageClick(imageData.url)}
                                    />
                                </div>
                            );
                        }
                    })}
                </div>
            </div>
        </div>
    );
});

RepairCostsImageGallery.displayName = 'RepairCostsImageGallery';

export default RepairCostsImageGallery;

